import React, { useEffect, useRef } from 'react'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import { UndoRedo } from '@tiptap/extensions'
import { canvasTextExtensions, toCanvasTextHtml } from '../utils/canvasTextSchema'

export interface CanvasTextEditorProps {
  /** Der gespeicherte Inhalt — HTML (ADR 0029) oder, bei einer älteren Zeile, Klartext. */
  content: string
  /** Die Beschriftung des Felds für Screenreader. */
  label: string
  /** Nach jeder Eingabe: das neue HTML und ob nichts mehr dasteht. */
  onChange: (html: string, empty: boolean) => void
  /** Escape oder ⌘/⌃+Enter — der Text ist fertig. */
  onExit: () => void
  /** Der Fokus geht — `relatedTarget` sagt, wohin (siehe `TextNode`). */
  onBlur: (event: FocusEvent) => void
  /** Der Editor steht (bzw. geht, `null`) — die **Options-Leiste** formatiert über ihn. */
  onReady?: (editor: Editor | null) => void
  /** Die Kennung des Texts — aus ihr folgt die Form seiner Marker (wie in Ruhe). */
  seed?: string
}

/**
 * Das Schreibfeld eines **Texts** — TipTap (ADR 0029), nur im
 * **Bearbeiten-Modus** gemountet. In Ruhe zeichnet `CanvasTextContent` den
 * Text; ein Editor je sichtbarem Text wäre auf einer vollen Pinnwand zu teuer.
 *
 * Das Schema teilt es mit dem Formatieren des ganzen Texts
 * (`canvasTextSchema.ts`). ⌘B/⌘I/⌘U/⌘⇧S schalten die Auszeichnungen, „- "
 * am Zeilenanfang macht eine Liste.
 *
 * **Jeder Tastendruck bleibt hier.** Die Kürzel der App und der Pinnwand
 * horchen am `window` und schweigen nur für `<input>` und `<textarea>` — ein
 * `contenteditable` kennen sie nicht. Ohne das stellte ein getipptes „n“ ein
 * Werkzeug scharf, und ⌘Z nähme statt eines Buchstabens eine Geste zurück.
 * ProseMirror hat den Tastendruck zu dem Zeitpunkt schon verarbeitet; gestoppt
 * wird nur das Weiterreichen.
 *
 * @example
 * <CanvasTextEditor content={draft} label="Text" onChange={…} onExit={…} onBlur={…} />
 */
export const CanvasTextEditor: React.FC<CanvasTextEditorProps> = ({ content, label, onChange, onExit, onBlur, onReady, seed }) => {
  // Die Rückrufe über Refs: Der Editor wird EINMAL gebaut, seine Optionen
  // sähen sonst für immer die ersten.
  const changeRef = useRef(onChange)
  changeRef.current = onChange
  const exitRef = useRef(onExit)
  exitRef.current = onExit
  const blurRef = useRef(onBlur)
  blurRef.current = onBlur

  const editor = useEditor({
    extensions: [...canvasTextExtensions({ seed }), UndoRedo],
    content: toCanvasTextHtml(content),
    // Fokus von Hand (unten): TipTaps eigenes `autofocus` scrollt die
    // Stelle ins Bild — auf der Pinnwand schöbe das den Ausschnitt.
    autofocus: false,
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        class: 'text-node__editor nodrag nopan nowheel',
        'aria-label': label,
        role: 'textbox',
        'aria-multiline': 'true',
      },
      handleDOMEvents: {
        keydown: (_view, event) => {
          event.stopPropagation()
          if (event.key === 'Escape' || (event.key === 'Enter' && (event.metaKey || event.ctrlKey))) {
            event.preventDefault()
            exitRef.current()
            return true
          }
          return false
        },
      },
    },
    onUpdate: ({ editor }) => changeRef.current(editor.getHTML(), editor.isEmpty),
    onBlur: ({ event }) => blurRef.current(event),
  })

  useEffect(() => {
    if (!editor) return
    editor.commands.focus('end', { scrollIntoView: false })
  }, [editor])

  const readyRef = useRef(onReady)
  readyRef.current = onReady
  useEffect(() => {
    readyRef.current?.(editor)
    return () => readyRef.current?.(null)
  }, [editor])

  return <EditorContent editor={editor} className="text-node__editor-host" />
}
